import type { GameView, PlayerCommand, Role } from "@coup/shared";
import type { ReactNode } from "react";
import { ROLE_ART } from "../../art";
import { TimerBar } from "../../components/TimerBar";
import { roleName, type Names } from "./names";
import styles from "./Table.module.css";

interface Props {
  view: GameView;
  names: Names;
  responseMs: number;
  onCommand: (cmd: PlayerCommand) => void;
  onTick?: (msLeft: number) => void;
}

/** What the current window is asking responders about. */
function claimText(view: GameView, n: Names): ReactNode {
  const p = view.phase;
  const a = view.action;
  if (p.t !== "window" || !a) return null;
  const you = view.you;
  const target = a.target === you ? "you" : <b>{n.name(a.target)}</b>;
  if (p.stage === "block") {
    const whose = a.actor === you ? "your" : `${n.name(a.actor)}'s`;
    return (
      <>
        <b>{n.name(p.claimant)}</b> claims <b>{roleName(p.claim)}</b> to block {whose}{" "}
        {actionNoun(a.kind)}.
      </>
    );
  }
  if (p.stage === "blockOnly") {
    return (
      <>
        <b>{n.name(a.actor)}</b>&apos;s <b>{roleName(a.claim)}</b> stands.{" "}
        {a.target === you ? "Block it?" : "Waiting for a block."}
      </>
    );
  }
  const actor = <b>{n.name(a.actor)}</b>;
  switch (a.kind) {
    case "foreignAid":
      return <>{actor} takes Foreign Aid.</>;
    case "tax":
      return (
        <>
          {actor} claims <b>Duke</b> to take 3 coins.
        </>
      );
    case "exchange":
      return (
        <>
          {actor} claims <b>Ambassador</b> to exchange cards.
        </>
      );
    case "steal":
      return (
        <>
          {actor} claims <b>Captain</b> to steal 2 coins from {target}.
        </>
      );
    case "assassinate":
      return (
        <>
          {actor} claims <b>Assassin</b> to assassinate {target}.
        </>
      );
    default:
      return null;
  }
}

const article = (role: Role) => (role === "ambassador" || role === "assassin" ? "an" : "a");

const actionNoun = (kind: string) =>
  kind === "foreignAid" ? "Foreign Aid" : kind === "steal" ? "steal" : "assassination";

function waitingText(view: GameView, n: Names): string {
  const p = view.phase;
  const me = view.players.find((x) => x.id === view.you);
  const prefix =
    p.t === "over"
      ? ""
      : !me
        ? "Spectating. "
        : me.cards === 0
          ? "You are out — watching the court. "
          : "";
  switch (p.t) {
    case "awaitAction":
      return `${prefix}Waiting for ${n.name(view.turn.playerId)} to play.`;
    case "window":
      return `${prefix}Waiting for responses…`;
    case "challenge":
      return `${prefix}Waiting for ${n.name(p.accused)} to reveal or concede.`;
    case "loseCard":
      return p.playerId === view.you
        ? "Choose a card to lose."
        : `${prefix}${n.name(p.playerId)} is choosing a card to lose.`;
    case "exchange":
      return p.playerId === view.you
        ? `Pick the ${view.hand.length} cards to keep.`
        : `${prefix}${n.name(p.playerId)} is choosing cards.`;
    case "over":
      return "Match over.";
  }
}

export function PromptDock({ view, names, responseMs, onCommand, onTick }: Props) {
  const o = view.options;
  const p = view.phase;
  const urgent = o.t === "declare" || o.t === "respond" || o.t === "challenged";

  let body: ReactNode;
  if (o.t === "declare") {
    body = (
      <>
        <div className={styles.dockTitle}>Your turn</div>
        <div className={styles.dockText}>
          {o.actions.some((a) => a.enabled && a.kind !== "coup")
            ? "Click the treasury to take coins, the deck to exchange, or a player to act on them."
            : "You have 10 or more coins: click a player to launch a Coup."}
        </div>
      </>
    );
  } else if (o.t === "respond" && p.t === "window") {
    const respond = (response: "allow" | "challenge" | { block: Role }) =>
      onCommand({ t: "respond", windowId: o.windowId, response });
    body = (
      <>
        <div className={styles.dockText}>{claimText(view, names)}</div>
        <TimerBar
          deadline={p.deadline}
          total={responseMs}
          height={10}
          showSeconds
          onTick={onTick}
        />
        {o.challenge && (
          <button
            type="button"
            className={styles.bullshitButton}
            onClick={() => respond("challenge")}
          >
            Bullshit!
          </button>
        )}
        {o.blocks.length > 0 && (
          <div style={{ display: "flex", gap: 6 }}>
            {o.blocks.map((r) => (
              <button
                key={r}
                type="button"
                className={styles.blockButton}
                style={{ background: ROLE_ART[r].color }}
                onClick={() => respond({ block: r })}
              >
                Block
                <br />
                claim {roleName(r)}
              </button>
            ))}
          </div>
        )}
        <button type="button" className={styles.dockGhost} onClick={() => respond("allow")}>
          Allow
        </button>
      </>
    );
  } else if (o.t === "challenged" && p.t === "challenge") {
    body = (
      <>
        <div className={styles.dockText}>
          <b>{names.name(p.challenger)}</b> says you don&apos;t hold {article(o.role)}{" "}
          <b>{roleName(o.role)}</b>.
        </div>
        <button
          type="button"
          className={styles.revealButton}
          disabled={!o.canReveal}
          onClick={() => onCommand({ t: "reveal" })}
        >
          Reveal {roleName(o.role)}
        </button>
        <button
          type="button"
          className={styles.dockGhost}
          onClick={() => onCommand({ t: "concede" })}
        >
          Concede — lose a card
        </button>
        <div className={styles.dockHint}>
          {o.canReveal
            ? `Reveal: ${names.name(p.challenger)} loses a card, and your ${roleName(o.role)} is shuffled away for a new one.`
            : `You don't hold ${article(o.role)} ${roleName(o.role)}.`}
        </div>
      </>
    );
  } else {
    body = (
      <>
        {p.t === "window" && <div className={styles.dockText}>{claimText(view, names)}</div>}
        {p.t === "window" && (
          <TimerBar deadline={p.deadline} total={responseMs} height={10} showSeconds />
        )}
        <div className={styles.dockText}>{waitingText(view, names)}</div>
      </>
    );
  }

  return (
    <div
      className={styles.dock}
      style={{ borderColor: urgent ? "var(--timer)" : "var(--line)" }}
      data-dock
    >
      {body}
    </div>
  );
}
